import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import RoundButton from "@app/component/RoundButton";
import UsersSelect from "@app/component/UsersSelect";
import useView from "@app/component/view/utils/useView";
import OrgOptions, { getOrgOptionsDisplayName } from "@app/scene/reports/enums/OrgOptions";
import {
  getCurrentUserId,
  isCurrentUserMember,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { userDisplayName } from "@app/util/formatters";
import getSelectedTeams from "@app/util/members/getSelectedTeams";
import { isSimpleCondition } from "@app/util/viewModel/assert";

const messages = defineMessages({
  multipleUsersOrTeams: {
    id: "reports.mileageTracking.filters.user.title.multipleUsers",
    defaultMessage: "Multiple Users or Teams",
    description:
      "Label in the Title of the User filter button on the Mileage Tracking page when different users are selected",
  },
  title: {
    id: "reports.mileageTracking.filters.user.title",
    defaultMessage: "Users{hasValue, select, true {: {label}} other {}}",
    description: "Title of the User filter button on the Mileage Tracking page",
  },
});

export interface UserFilterProps {
  currentUserId: User["id"];
  isCurrentUserOwner: boolean;
  isRep: boolean;
  teams: Team[];
  users: User[];
}

const UserFilter: FC<UserFilterProps> = ({
  currentUserId,
  isCurrentUserOwner,
  isRep,
  teams,
  users,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    updateFilterModel,
    viewState: { filter },
  } = useView();

  const condition = filter[TripFieldName.USER];
  const value = useMemo(() => {
    return condition &&
      isSimpleCondition(condition) &&
      condition.operator === FilterOperator.CONTAINS
      ? (condition.value as User["id"][])
      : undefined;
  }, [condition]);

  useEffect(() => {
    if (!value?.length) {
      // using setTimeout here because of a race condition,
      // we need to apply this filter _after_ the initial
      // viewState is read from the local storage
      setTimeout(() => {
        updateFilterModel({
          [TripFieldName.USER]: {
            operator: FilterOperator.CONTAINS,
            value: isRep ? [currentUserId] : users.map(({ id }) => id),
          },
        });
      }, 0);
    }
  }, [value, currentUserId, updateFilterModel, isRep, users]);

  const label = useMemo(() => {
    if (!value?.length) {
      return "";
    }
    const userIds = new Set(value);
    if (isCurrentUserOwner && users.every((user) => userIds.has(user.id))) {
      return getOrgOptionsDisplayName(intl, OrgOptions.ENTIRE_ORG);
    } else if (value.length === 1) {
      return userDisplayName(users.find(({ id }) => id === value[0])!);
    }

    const selectedTeams = getSelectedTeams(teams, value);
    // all selected users belong to a single team
    const isOnlyASingleTeamSelected =
      selectedTeams.length === 1 &&
      value.every((id) => selectedTeams[0].users.some((user) => user.id === id));

    if (isOnlyASingleTeamSelected) {
      return selectedTeams[0].name;
    } else {
      return intl.formatMessage(messages.multipleUsersOrTeams);
    }
  }, [value, isCurrentUserOwner, users, teams, intl]);

  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);
  const handleHide = useCallback(() => setPopoverVisibility(false), []);

  const filteredUsers = useMemo(
    () => users.filter((user) => (isOwner(user) ? isCurrentUserOwner : true)),
    [isCurrentUserOwner, users]
  );

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      analytics.using(["Users Filter"]);
      updateFilterModel({
        [TripFieldName.USER]: { operator: FilterOperator.CONTAINS, value: userIds },
      });
      handleHide();
    },
    [analytics, handleHide, updateFilterModel]
  );

  if (isRep) {
    return null;
  }

  return (
    <Popover
      content={
        <UsersSelect
          isCurrentUserOwner={isCurrentUserOwner}
          onApply={handleApply}
          onCancel={handleHide}
          teams={teams}
          userIds={value}
          users={filteredUsers}
        />
      }
      destroyTooltipOnHide
      onOpenChange={setPopoverVisibility}
      open={popoverVisible}
      trigger={["click"]}
    >
      <RoundButton active={!!value?.length} hasMenu>
        {intl.formatMessage(messages.title, { hasValue: !!value?.length, label })}
      </RoundButton>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  isCurrentUserOwner: isCurrentUserOwner(state),
  isRep: isCurrentUserMember(state) || isCurrentUserReadOnly(state),
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(UserFilter);
